import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Wind, ArrowRight, Phone, Calendar, Check, Shield, Clock, Award, Users, DollarSign, MapPin, HelpCircle, AlertTriangle } from 'lucide-react';
import { localBusinessSchema, generateFAQSchema, generateServiceSchema, generateBreadcrumbSchema } from '../../utils/schemaMarkup';
import { ServiceVideoSection } from '../../components/services/ServiceVideoSection';
import { useSchemaMarkup } from '../../utils/useSchemaMarkup';
import Breadcrumb from '../../components/ui/Breadcrumb';
import { PhoneLinkText } from '../../components/PhoneLinkText';

const serviceAreas = [
  { name: 'Lexington', slug: 'lexington' },
  { name: 'Nicholasville', slug: 'nicholasville' },
  { name: 'Georgetown', slug: 'georgetown' },
  { name: 'Richmond', slug: 'richmond' },
  { name: 'Frankfort', slug: 'frankfort' },
  { name: 'Versailles', slug: 'versailles' },
  { name: 'Winchester', slug: 'winchester' },
  { name: 'Paris', slug: 'paris' },
  { name: 'Danville', slug: 'danville' },
  { name: 'Harrodsburg', slug: 'harrodsburg' },
  { name: 'Berea', slug: 'berea' },
  { name: 'Mt Sterling', slug: 'mt-sterling' },
  { name: 'Lawrenceburg', slug: 'lawrenceburg' },
  { name: 'Midway', slug: 'midway' },
  { name: 'Wilmore', slug: 'wilmore' },
  { name: 'Lancaster', slug: 'lancaster' },
  { name: 'Cynthiana', slug: 'cynthiana' }
];

const subServices = [
  { slug: 'air-filtration', title: 'Air Filtration', desc: 'Capture dust, pollen, and allergens with the right filtration for your HVAC system.' },
  { slug: 'uv-air-purifiers', title: 'UV Air Purifiers', desc: 'UV treatment options for addressing biological contaminants in your air.' },
  { slug: 'humidity-control', title: 'Humidity Control', desc: 'Balance moisture levels year-round with whole-house humidifiers and dehumidifiers.' },
  { slug: 'duct-cleaning', title: 'Duct Cleaning', desc: 'Professional evaluation and cleaning when your ducts have significant contamination.' },
  { slug: 'ventilation', title: 'Ventilation Solutions', desc: 'Bring fresh outdoor air into your home while managing energy loss.' },
  { slug: 'air-balancing', title: 'Air Balancing', desc: 'Fix uneven temperatures by optimizing airflow distribution throughout your home.' }
];

// Content for each service/subservice
const getContent = (key) => {
  const content = {
    main: {
      title: 'Air Quality Services',
      metaTitle: 'Indoor Air Quality Services Lexington KY | Filtration, Humidity & Ventilation | ComfortMaster',
      metaDescription: 'Indoor air quality solutions in Lexington, Nicholasville & Central Kentucky. Filtration, UV air treatment, humidity control, duct cleaning, ventilation. Call (859) 357-6803.',
      description: 'Temperature is only one part of a comfortable home. Dust, allergens, humidity, and stale air can affect your family\'s comfort and health.',
      extendedDescription: 'Indoor air can be 2-5 times more polluted than outdoor air. Central Kentucky homes face challenges from seasonal allergens, humidity extremes, and tightly sealed construction.',
      signs: ['Excessive dust even after cleaning', 'Family members with allergies or asthma', 'Musty or stale odors', 'Condensation on windows', 'Dry skin and static electricity', 'Visible mold growth', 'Rooms that feel stuffy', 'Symptoms improve when away'],
      faqs: [
        { q: 'What affects indoor air quality?', a: 'Key factors include particulates (dust, pollen, pet dander), humidity levels, ventilation, and volatile organic compounds (VOCs).' },
        { q: 'How do I know if air quality is poor?', a: 'Signs include excessive dust, musty odors, condensation, aggravated allergies, and symptoms that improve when you leave.' },
        { q: 'Will a better filter help?', a: 'Often yes, though higher-efficiency filters may restrict airflow if your system isn\'t designed for them.' },
        { q: 'Do UV purifiers work?', a: 'UV-C light can neutralize certain microorganisms when properly applied.' }
      ]
    },
    'air-filtration': {
      title: 'Air Filtration',
      metaTitle: 'Home Air Filtration Solutions Lexington KY | HVAC Filters | ComfortMaster',
      metaDescription: 'Air filtration solutions in Lexington & Central Kentucky. Improve indoor air quality with proper HVAC filtration. Call (859) 357-6803.',
      description: 'Your HVAC filter is the first line of defense against airborne particles. The right filtration solution can significantly reduce dust, pollen, pet dander, and other irritants.',
      extendedDescription: 'Filter effectiveness is measured by MERV rating. Standard filters are MERV 1-4, good quality are MERV 8-11, high-efficiency are MERV 13+. We help you find the right balance for your system.',
      signs: ['Dust accumulates quickly after cleaning', 'Allergy symptoms worse indoors', 'Pet dander is a concern', 'Current filter looks dirty quickly', 'HVAC system seems to work harder', 'Visible dust around vents', 'Respiratory issues in household', 'New construction dust'],
      faqs: [
        { q: 'What MERV rating should I use?', a: 'Most homes do well with MERV 8-11 filters. MERV 13+ provides better filtration but requires compatible equipment. Using too high a MERV can restrict airflow and damage your system.' },
        { q: 'How often should I change my filter?', a: 'Standard 1-inch filters every 1-3 months. 4-inch media filters may last 6-12 months. Homes with pets or allergies need more frequent changes.' },
        { q: 'What about whole-house air cleaners?', a: 'Media air cleaners use deep pleated filters that capture more particles without restricting airflow. They\'re installed in your ductwork.' },
        { q: 'Are electronic air cleaners worth it?', a: 'They\'re effective when clean but require regular cleaning to maintain performance. They work best as part of a comprehensive approach.' }
      ],
      process: ['Evaluate your current filtration', 'Assess your HVAC system compatibility', 'Recommend appropriate MERV rating', 'Install filter or air cleaner', 'Test airflow and system operation', 'Provide maintenance schedule']
    },
    'uv-air-purifiers': {
      title: 'UV Air Purifiers',
      metaTitle: 'UV Air Treatment Lexington KY | UV Air Purifiers | ComfortMaster',
      metaDescription: 'UV air treatment in Lexington & Central Kentucky. Understand UV purification options for your HVAC system. Call (859) 357-6803.',
      description: 'UV-C light has been used for decades in hospitals to neutralize microorganisms. Residential UV systems can be added to your HVAC system to help address biological contaminants.',
      extendedDescription: 'UV systems are installed near the evaporator coil (to keep it clean and inhibit mold) or in the return duct (to treat circulating air). Effectiveness depends on proper sizing and placement.',
      signs: ['Musty odors from HVAC system', 'Visible mold on AC coil or in ducts', 'Family members sensitive to mold', 'Home feels stuffy', 'Recurring respiratory issues', 'Pets in the home', 'Concern about airborne germs', 'Desire for additional air treatment'],
      faqs: [
        { q: 'How do UV air purifiers work?', a: 'UV-C light (wavelength 254nm) damages the DNA of microorganisms, neutralizing bacteria, mold spores, and viruses as they pass by.' },
        { q: 'Where is the UV light installed?', a: 'Coil-mounted UV lights shine on the evaporator coil to prevent mold. Air-treatment lights are placed in the return duct to treat circulating air.' },
        { q: 'Do UV lights need maintenance?', a: 'UV bulbs lose effectiveness over time and should be replaced annually, usually during your spring maintenance visit.' },
        { q: 'Will UV light eliminate odors?', a: 'Standard UV-C lights don\'t address odors directly. Some systems include photocatalytic oxidation that can help with VOCs and odors.' }
      ],
      process: ['Evaluate your specific concerns', 'Inspect evaporator coil and ductwork', 'Recommend appropriate UV system', 'Professional installation', 'System testing and verification', 'Provide maintenance schedule']
    },
    'humidity-control': {
      title: 'Humidity Control',
      metaTitle: 'Indoor Humidity Control Lexington KY | Humidifiers & Dehumidifiers | ComfortMaster',
      metaDescription: 'Humidity control solutions in Lexington & Central Kentucky. Address excess moisture or dry air in your home. Call (859) 357-6803.',
      description: 'Central Kentucky experiences both humid summers and dry winters. The right humidity level (30-50%) affects comfort, health, and even your home itself.',
      extendedDescription: 'High humidity promotes mold growth, dust mites, and that sticky uncomfortable feeling. Low humidity causes dry skin, static electricity, and can damage wood floors and furniture.',
      signs: ['Condensation on windows in winter', 'Dry skin, chapped lips, static shocks', 'Hardwood floors cracking or gapping', 'Home feels clammy in summer', 'Musty odors (high humidity)', 'Visible mold growth', 'Wallpaper peeling or paint bubbling', 'Respiratory discomfort'],
      faqs: [
        { q: 'What humidity level should I maintain?', a: 'The ideal range is 30-50% relative humidity year-round. In winter, you may need 30-40% to prevent condensation on windows.' },
        { q: 'How does a whole-house humidifier work?', a: 'It connects to your ductwork and water supply. When humidity is low, water is introduced into the airstream and distributed throughout your home.' },
        { q: 'Do I need a dehumidifier if I have AC?', a: 'AC removes some humidity, but may not be enough in very humid conditions. A dedicated dehumidifier provides more precise control.' },
        { q: 'What type of humidifier is best?', a: 'Bypass and fan-powered are common and affordable. Steam humidifiers provide the most precise control but cost more.' }
      ],
      process: ['Assess current humidity levels', 'Identify problem areas in your home', 'Recommend appropriate solution', 'Professional installation', 'Calibrate humidistat', 'Provide maintenance instructions']
    },
    'duct-cleaning': {
      title: 'Duct Cleaning',
      metaTitle: 'Duct Cleaning Services Lexington KY | Professional Duct Cleaning | ComfortMaster',
      metaDescription: 'Professional duct cleaning in Lexington & Central Kentucky. Honest evaluation and quality service. Call (859) 357-6803.',
      description: 'When significant debris, mold, or contamination is present in your duct system, professional cleaning may be appropriate. We provide honest assessment.',
      extendedDescription: 'The EPA notes that ducts don\'t need routine cleaning. However, cleaning is recommended when there\'s visible mold, vermin infestation, or significant debris accumulation.',
      signs: ['Visible mold growth inside ducts', 'Evidence of rodent or insect infestation', 'Substantial debris visible in ducts', 'Ducts never cleaned after construction', 'Recent renovation created significant dust', 'Family member with severe allergies wants evaluation', 'Visible dust puffs when system starts'],
      faqs: [
        { q: 'How often should ducts be cleaned?', a: 'There\'s no set schedule. Ducts should be cleaned when there\'s a specific reason - visible contamination, mold, vermin, or post-construction debris.' },
        { q: 'Will duct cleaning improve my air quality?', a: 'If your ducts have significant contamination, cleaning can help. For normal dust, changing filters regularly is more effective.' },
        { q: 'How is professional duct cleaning done?', a: 'We use specialized equipment to dislodge debris and a high-powered vacuum to remove it. All supply and return ducts, registers, and accessible components are cleaned.' },
        { q: 'How do I know if I need duct cleaning?', a: 'We can inspect your ducts and show you what we find. If there\'s significant debris or contamination, we\'ll recommend cleaning. If not, we\'ll tell you.' }
      ],
      process: ['Visual inspection with camera', 'Honest assessment of need', 'Protect home furnishings', 'Agitation to loosen debris', 'High-powered vacuum extraction', 'Clean registers and grilles', 'Before and after documentation']
    },
    'ventilation': {
      title: 'Ventilation Solutions',
      metaTitle: 'Home Ventilation Solutions Lexington KY | Fresh Air Systems | ComfortMaster',
      metaDescription: 'Ventilation solutions in Lexington & Central Kentucky. Improve indoor air freshness. Call (859) 357-6803.',
      description: 'Modern homes are built tight, which saves energy but can trap stale air and pollutants inside. Proper ventilation brings in fresh air while managing energy loss.',
      extendedDescription: 'Options range from exhaust fans to HRVs and ERVs that exchange indoor and outdoor air while transferring heat and moisture. We help you find the right approach.',
      signs: ['Home feels stuffy even with HVAC running', 'Odors linger longer than they should', 'Excess moisture in bathrooms or kitchen', 'Windows fog up frequently', 'New construction or very tight home', 'Carbon dioxide levels feel high', 'Desire for fresh outdoor air', 'Concerns about indoor pollutants'],
      faqs: [
        { q: 'What is an HRV or ERV?', a: 'Heat Recovery Ventilators (HRV) and Energy Recovery Ventilators (ERV) exchange stale indoor air with fresh outdoor air while transferring heat between the streams.' },
        { q: 'What\'s the difference between HRV and ERV?', a: 'HRVs transfer heat only. ERVs transfer both heat and moisture. In Kentucky\'s climate, an ERV is often preferred for managing humidity in both seasons.' },
        { q: 'Does my home need mechanical ventilation?', a: 'Very tight new homes often do. Older, leakier homes may get adequate ventilation through infiltration. We can assess your situation.' },
        { q: 'Will ventilation increase my energy bills?', a: 'HRV/ERV systems recover 70-80% of the energy from exhausted air, making them much more efficient than simply opening windows.' }
      ],
      process: ['Assess your home\'s tightness', 'Identify ventilation needs', 'Recommend appropriate solution', 'Professional installation', 'Balance and commission system', 'Provide maintenance schedule']
    },
    'air-balancing': {
      title: 'Air Balancing',
      metaTitle: 'Air Balancing Services Lexington KY | HVAC Airflow Optimization | ComfortMaster',
      metaDescription: 'Air balancing services in Lexington & Central Kentucky. Fix uneven temperatures throughout your home. Call (859) 357-6803.',
      description: 'If some rooms are too hot while others are too cold, your system may have an airflow imbalance. Air balancing adjusts the distribution of conditioned air.',
      extendedDescription: 'Uneven temperatures often result from duct problems, improper register placement, or systems that weren\'t properly balanced during installation. We measure airflow and adjust as needed.',
      signs: ['Rooms that are always too hot or cold', 'Second floor much warmer than first', 'Some registers blow harder than others', 'Rooms far from the system are uncomfortable', 'Closing vents doesn\'t seem to help', 'Temperature varies significantly room to room', 'New addition doesn\'t get enough airflow'],
      faqs: [
        { q: 'Why are some rooms always hotter or colder?', a: 'Common causes include undersized ducts to distant rooms, supply/return imbalance, improper damper settings, duct leaks, or inadequate insulation.' },
        { q: 'Will closing vents in unused rooms help?', a: 'Closing too many vents can actually make problems worse by increasing duct pressure and reducing efficiency. Proper balancing is better.' },
        { q: 'What does air balancing involve?', a: 'We measure airflow at each supply register, compare to what each room needs, and adjust dampers, registers, or ductwork to achieve proper distribution.' },
        { q: 'Can duct modifications improve airflow?', a: 'Sometimes. If ducts are undersized, too long, or poorly routed, modifications may help. We\'ll explain what\'s needed and whether it justifies the cost.' }
      ],
      process: ['Measure airflow at each register', 'Calculate room-by-room requirements', 'Identify imbalances', 'Adjust dampers and registers', 'Recommend duct modifications if needed', 'Verify balanced temperatures']
    }
  };
  return content[key] || content.main;
};

const AirQualityPage = () => {
  const { subservice } = useParams();
  const current = getContent(subservice || 'main');
  const isMain = !subservice;

  // Generate schema markup
  const faqSchema = current.faqs ? generateFAQSchema(current.faqs) : null;
  const serviceSchema = generateServiceSchema(
    current.title,
    current.description,
    `https://comfortmasterheating.com/services/air-quality${subservice ? '/' + subservice : ''}`
  );
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: 'https://comfortmasterheating.com' },
    { name: 'Services', url: 'https://comfortmasterheating.com/services' },
    { name: 'Air Quality', url: 'https://comfortmasterheating.com/services/air-quality' },
    ...(subservice ? [{ name: current.title, url: `https://comfortmasterheating.com/services/air-quality/${subservice}` }] : [])
  ]);

  // Inject schemas into document head (bypasses react-helmet-async script issues)
  useSchemaMarkup(
    [localBusinessSchema, serviceSchema, breadcrumbSchema, faqSchema],
    `air-quality-${subservice || 'main'}`
  );

  return (
    <main>
      <Helmet>
        <title>{current.metaTitle}</title>
        <meta name="description" content={current.metaDescription} />
        <link rel="canonical" href={`https://comfortmasterheating.com/services/air-quality${subservice ? '/' + subservice : ''}`} />
      </Helmet>

      <section className="bg-gradient-to-br from-purple-600 to-indigo-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb 
            items={[
              { label: 'Services', href: '/services' },
              { label: 'Air Quality', href: '/services/air-quality' },
              ...(subservice ? [{ label: current.title }] : [])
            ]}
            className="mb-4 text-purple-200"
            linkClassName="hover:text-white transition-colors"
            separatorClassName="mx-1 opacity-60"
            currentClassName="text-white"
          />
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <Wind className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl">{current.title}</h1>
          </div>
          <p className="text-xl text-purple-100 max-w-3xl mb-4">{current.description}</p>
          {current.extendedDescription && <p className="text-lg text-purple-100/80 max-w-3xl mb-8">{current.extendedDescription}</p>}
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/contact" className="btn-primary bg-white text-purple-700 hover:bg-purple-50" data-testid="airquality-estimate-btn">
              <Calendar size={20} className="inline mr-2" />Discuss Your Air Quality Concerns
            </Link>
            <a href="tel:8593576803" className="btn-hero-secondary inline-flex items-center justify-center gap-2 hover:text-purple-700" data-testid="airquality-call-btn">
              <Phone size={20} className="mr-2" />(859) 357-6803
            </a>
          </div>
        </div>
      </section>

      <section className="py-16 bg-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {isMain ? (
            <>
              <div className="text-center mb-12">
                <h2 className="font-heading font-bold text-3xl text-slate-900 mb-4">Our Air Quality Services</h2>
                <p className="text-slate-600 max-w-2xl mx-auto">Improve your indoor comfort with solutions that address filtration, humidity, ventilation, and air distribution throughout Lexington, Nicholasville, and Central Kentucky.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
                {subServices.map((service, index) => (
                  <Link key={service.slug} to={`/services/air-quality/${service.slug}`} className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 group" data-testid={`airquality-sub-${index}`}>
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-purple-600 transition-colors">
                      <Wind className="w-6 h-6 text-purple-600 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-heading font-bold text-xl text-slate-900 mb-2">{service.title}</h3>
                    <p className="text-slate-600 text-sm mb-4">{service.desc}</p>
                    <span className="inline-flex items-center gap-2 text-purple-600 font-semibold text-sm">Learn More <ArrowRight size={16} /></span>
                  </Link>
                ))}
              </div>

              {current.signs && (
                <div className="bg-white rounded-2xl p-8 md:p-12 mb-12">
                  <div className="flex items-center gap-3 mb-6">
                    <AlertTriangle className="w-8 h-8 text-purple-600" />
                    <h2 className="font-heading font-bold text-2xl text-slate-900">Signs of Air Quality Problems</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {current.signs.map((sign, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check size={14} className="text-purple-600" />
                        </div>
                        <span className="text-slate-700">{sign}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-white rounded-2xl p-8 md:p-12 mb-12">
                <h2 className="font-heading font-bold text-2xl text-slate-900 mb-8 text-center">Why Central Kentucky Chooses ComfortMaster</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {[
                    { icon: Award, title: 'NATE Certified', desc: 'Technicians who understand air quality' },
                    { icon: Clock, title: 'Thorough Evaluation', desc: 'We identify the root cause first' },
                    { icon: Users, title: '25+ Years Local', desc: 'Serving Lexington & Central KY since 1999' },
                    { icon: DollarSign, title: 'Honest Advice', desc: 'We recommend only what you need' }
                  ].map((item, idx) => (
                    <div key={idx} className="text-center">
                      <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <item.icon className="w-7 h-7 text-purple-600" />
                      </div>
                      <h3 className="font-bold text-slate-900 mb-1">{item.title}</h3>
                      <p className="text-slate-600 text-sm">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {current.faqs && (
                <div className="bg-white rounded-2xl p-8 md:p-12 mb-12">
                  <div className="flex items-center gap-3 mb-8">
                    <HelpCircle className="w-8 h-8 text-purple-600" />
                    <h2 className="font-heading font-bold text-2xl text-slate-900">Frequently Asked Questions</h2>
                  </div>
                  <div className="space-y-6">
                    {current.faqs.map((faq, index) => (
                      <div key={index} className="border-b border-stone-200 pb-6 last:border-0">
                        <h3 className="font-bold text-slate-900 mb-2">{faq.q}</h3>
                        <p className="text-slate-600"><PhoneLinkText text={faq.a} /></p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Enhanced Service Areas with City Links */}
              <div className="bg-slate-900 rounded-2xl p-8 md:p-12">
                <div className="flex items-center gap-3 mb-6">
                  <MapPin className="w-8 h-8 text-purple-400" />
                  <h2 className="font-heading font-bold text-2xl text-white">Air Quality Service Areas</h2>
                </div>
                <p className="text-stone-300 mb-6">ComfortMaster provides indoor air quality services throughout Central Kentucky. Click on your city for local information:</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-8">
                  {serviceAreas.map((area) => (
                    <Link key={area.slug} to={`/service-areas/${area.slug}`} className="bg-slate-800 hover:bg-purple-600 text-white px-4 py-2 rounded-lg text-sm text-center transition-colors">
                      {area.name}
                    </Link>
                  ))}
                </div>
                <div className="border-t border-slate-700 pt-6">
                  <h3 className="text-white font-semibold mb-3">Popular Air Quality Services by City:</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                    <div>
                      <Link to="/service-areas/lexington" className="text-purple-400 hover:text-purple-300">Air Filtration Lexington KY</Link>
                    </div>
                    <div>
                      <Link to="/service-areas/nicholasville" className="text-purple-400 hover:text-purple-300">Humidity Control Nicholasville KY</Link>
                    </div>
                    <div>
                      <Link to="/service-areas/georgetown" className="text-purple-400 hover:text-purple-300">Duct Cleaning Georgetown KY</Link>
                    </div>
                    <div>
                      <Link to="/service-areas/richmond" className="text-purple-400 hover:text-purple-300">UV Air Purifier Richmond KY</Link>
                    </div>
                    <div>
                      <Link to="/service-areas/frankfort" className="text-purple-400 hover:text-purple-300">Ventilation Services Frankfort KY</Link>
                    </div>
                    <div>
                      <Link to="/service-areas/versailles" className="text-purple-400 hover:text-purple-300">Air Balancing Versailles KY</Link>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-8">
                <div className="bg-white p-8 rounded-2xl shadow-sm">
                  <h2 className="font-heading font-bold text-2xl text-slate-900 mb-6">About {current.title}</h2>
                  <p className="text-slate-700 mb-4">{current.description}</p>
                  {current.extendedDescription && <p className="text-slate-600">{current.extendedDescription}</p>}
                </div>

                {current.signs && (
                  <div className="bg-white p-8 rounded-2xl shadow-sm">
                    <div className="flex items-center gap-3 mb-6">
                      <AlertTriangle className="w-6 h-6 text-purple-600" />
                      <h2 className="font-heading font-bold text-2xl text-slate-900">Signs You May Need {current.title}</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {current.signs.map((sign, index) => (
                        <div key={index} className="flex items-start gap-3">
                          <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check size={14} className="text-purple-600" />
                          </div>
                          <span className="text-slate-700 text-sm">{sign}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {current.process && (
                  <div className="bg-white p-8 rounded-2xl shadow-sm">
                    <h2 className="font-heading font-bold text-2xl text-slate-900 mb-6">Our Process</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {current.process.map((item, index) => (
                        <div key={index} className="flex items-start gap-3">
                          <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check size={14} className="text-purple-600" />
                          </div>
                          <span className="text-slate-700 text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {current.faqs && (
                  <div className="bg-white p-8 rounded-2xl shadow-sm">
                    <div className="flex items-center gap-3 mb-6">
                      <HelpCircle className="w-6 h-6 text-purple-600" />
                      <h2 className="font-heading font-bold text-2xl text-slate-900">Common Questions</h2>
                    </div>
                    <div className="space-y-6">
                      {current.faqs.map((faq, index) => (
                        <div key={index} className="border-b border-stone-200 pb-6 last:border-0">
                          <h3 className="font-bold text-slate-900 mb-2">{faq.q}</h3>
                          <p className="text-slate-600 text-sm"><PhoneLinkText text={faq.a} /></p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* City-specific service links for sub-pages */}
                <div className="bg-slate-100 p-6 rounded-2xl">
                  <h3 className="font-bold text-slate-900 mb-4">{current.title} Available In:</h3>
                  <div className="flex flex-wrap gap-2">
                    {serviceAreas.slice(0, 8).map((area) => (
                      <Link key={area.slug} to={`/service-areas/${area.slug}`} className="text-sm bg-white px-3 py-1 rounded-full text-slate-600 hover:bg-purple-100 hover:text-purple-700 transition-colors">
                        {area.name}, KY
                      </Link>
                    ))}
                    <Link to="/service-areas" className="text-sm bg-purple-600 px-3 py-1 rounded-full text-white hover:bg-purple-700 transition-colors">
                      + More Cities
                    </Link>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-1">
                <div className="bg-white p-6 rounded-2xl shadow-lg sticky top-24">
                  <h3 className="font-heading font-bold text-xl text-slate-900 mb-6">Request an Evaluation</h3>
                  <div className="space-y-4 mb-6">
                    {[
                      { icon: Shield, title: 'Licensed & Insured', desc: 'Fully licensed Kentucky technicians' },
                      { icon: Clock, title: 'Thorough Assessment', desc: 'We evaluate before recommending' },
                      { icon: Award, title: 'NATE Certified', desc: 'Industry-leading certification' }
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <item.icon className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                          <p className="text-slate-600 text-xs">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Link to="/contact" className="btn-primary w-full text-center block mb-3" data-testid="airquality-cta-btn">Request Evaluation</Link>
                  <a href="tel:8593576803" className="btn-secondary w-full text-center block text-sm">
                    <Phone size={16} className="inline mr-2" />(859) 357-6803
                  </a>
                  <div className="mt-8 pt-6 border-t border-stone-200">
                    <h4 className="font-bold text-slate-900 mb-4 text-sm">Other Air Quality Services</h4>
                    <div className="space-y-2">
                      {subServices.filter(s => s.slug !== subservice).map(s => (
                        <Link key={s.slug} to={`/services/air-quality/${s.slug}`} className="block text-sm text-slate-600 hover:text-purple-600">→ {s.title}</Link>
                      ))}
                    </div>
                  </div>
                  <div className="mt-8 pt-6 border-t border-stone-200">
                    <h4 className="font-bold text-slate-900 mb-4 text-sm">Service Areas</h4>
                    <p className="text-slate-600 text-xs mb-3">Serving Lexington, Nicholasville, Georgetown, and all of Central Kentucky.</p>
                    <Link to="/service-areas" className="text-purple-600 text-sm font-semibold hover:text-purple-700">View All Areas →</Link>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <ServiceVideoSection />

      <section className="py-16 bg-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-3xl mb-4">Ready to Improve Your Indoor Air Quality?</h2>
          <p className="text-purple-100 mb-6 max-w-2xl mx-auto">Contact ComfortMaster today for expert air quality services in Lexington, Nicholasville, and throughout Central Kentucky.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary bg-white text-purple-700 hover:bg-purple-50">Request Evaluation</Link>
            <Link to="/specials" className="btn-secondary border-white/30 text-white hover:bg-white hover:text-purple-700">View Current Specials</Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AirQualityPage;
